import React from "react";
import { CustomText } from "../../../components";
import { fonts } from "../../../config";
import { masks, validations } from "../../../utils";
import { StylesGuest } from "../styles";
import Grid from "@mui/material/GridLegacy";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  ClearButton,
  SubmitButton,
  InputText,
  InputSelect,
  InputDatePicker,
  InputPhone,
} from "../../../components/newComponents";

const schema = z
  .object({
    fullName: z
      .string()
      .min(1, "Campo Obrigatório")
      .regex(
        /^[A-zÀ-Ÿ][A-zÀ-ÿ']+\s([A-zÀ-ÿ']\s?)*[A-zÀ-Ÿ][A-zÀ-ÿ']+$/,
        "Nome inválido (ex: João Silva)"
      ),
    email: z.string().min(1, "Campo Obrigatório").email("E-mail inválido"),
    docType: z.number(),
    docValue: z.string().default(""),
    birthday: z
      .date({ error: "Campo Obrigatório" })
      .max(new Date(), "Data inválida"),
    phone: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.docType === 1) {
      const err = validations.validateCPF(data.docValue || "");
      if (err !== null) {
        ctx.addIssue({
          code: "custom",
          message: err || "CPF inválido",
          path: ["docValue"],
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

function GuestForm({
  data,
  onSubmit,
  onBack,
  initialValues,
}: {
  data: any;
  onSubmit: (v: any) => void;
  onBack?: () => void;
  initialValues?: any;
}) {
  const [resetKey, setResetKey] = React.useState(0);
  const { control, handleSubmit, watch, reset, setValue, trigger } =
    useForm<FormValues>({
      resolver: zodResolver(schema) as any,
      defaultValues: {
        fullName: initialValues?.fullName ?? data?.secondaryGuests?.name ?? "",
        email: initialValues?.email ?? data?.secondaryGuests?.email ?? "",
        docType: initialValues?.docType ?? (data?.secondaryGuests?.document ? 1 : data?.secondaryGuests?.passport ? 2 : 1),
        docValue: initialValues?.docValue ?? data?.secondaryGuests?.document ?? data?.secondaryGuests?.passport ?? "",
        birthday: initialValues?.birthday ?? (data?.secondaryGuests?.birthDate ? new Date(data.secondaryGuests.birthDate + "T00:00:00") : undefined),
        phone: initialValues?.phone ?? data?.secondaryGuests?.phone ?? "",
      },
    });

  const watchedDocType = watch("docType");

  const onSubmitHandler = (formValues: FormValues) => {
    onSubmit(formValues);
  };

  return (
    <Grid container justifyContent="center">

      <StylesGuest.Container>
        <form
          onSubmit={handleSubmit(onSubmitHandler)}
          style={{ width: "100%" }}
        >
          <Grid alignItems="flex-end" container spacing={1}>
            <Grid item xs={6} md={6}>
              <Controller
                name="fullName"
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    label="NOME COMPLETO"
                    error={fieldState.error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    label="E-MAIL"
                    error={fieldState.error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <Controller
                name="docType"
                control={control}
                render={({ field, fieldState }) => (
                  <InputSelect
                    label="TIPO DO DOCUMENTO"
                    error={fieldState.error?.message}
                    value={field.value}
                    options={[
                      { value: 1, label: "CPF" },
                      { value: 2, label: "Passaporte" },
                    ]}
                    onChange={(value) => {
                      field.onChange(Number(value));
                      setValue("docValue", "");
                      trigger("docValue");
                    }}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <Controller
                name="docValue"
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    label={watchedDocType === 1 ? "CPF" : "PASSAPORTE"}
                    error={fieldState.error?.message}
                    value={field.value}
                    onChange={(e) => {
                      const value =
                        watchedDocType === 1
                          ? masks.CPF(e.target.value)
                          : e.target.value;
                      field.onChange(value);
                    }}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <Controller
                key={resetKey}
                name="birthday"
                control={control}
                render={({ field, fieldState }) => (
                  <InputDatePicker
                    label="DATA NASCIMENTO"
                    error={fieldState.error?.message}
                    value={field.value ?? null}
                    onChange={(date) => field.onChange(date ?? undefined)}
                    onBlur={field.onBlur}
                    maxDate={new Date()}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} md={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field, fieldState }) => (
                  <InputPhone
                    label="Telefone"
                    error={fieldState.error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="TELEFONE"
                  />
                )}
              />
            </Grid>
          </Grid>

          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "8px",
              marginBottom: "16px",
              marginTop: "16px",
            }}
          >
            {onBack && (
              <ClearButton
                fullWidth={false}
                variant="text"
                type="button"
                onClick={onBack}
              >
                Voltar
              </ClearButton>
            )}
            <ClearButton
              fullWidth={false}
              variant="text"
              type="button"
              onClick={() => { reset({ fullName: "", email: "", docType: 1, docValue: "", birthday: undefined, phone: "" }); setResetKey((k) => k + 1); }}
            >
              Limpar Campos
            </ClearButton>
            <SubmitButton fullWidth={false} type="submit">
              Confirmar
            </SubmitButton>
          </div>
        </form>
      </StylesGuest.Container>

    </Grid>
  );
}

export default GuestForm;
